import { Sparkles, Ear, FlaskConical, HeartPulse, Pill, Stethoscope, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n";

const DERM_IMG = "/clinic-room.jpg";
const ENT_IMG =
    "https://images.unsplash.com/photo-1673865641073-4479f93a7776?q=80&w=1200&auto=format&fit=crop";
const PATH_IMG =
    "https://images.unsplash.com/photo-1606206591513-adbfbdd7a177?q=80&w=1200&auto=format&fit=crop";

function Tag({ children, light = false }) {
    return (
        <span
            className={`inline-block rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
                light ? "bg-white/10 text-brand-lime" : "bg-brand-olivelight text-brand-olive"
            }`}
        >
            {children}
        </span>
    );
}

export default function Services() {
    const { t } = useLang();
    const s = t.services.items;

    return (
        <section id="services" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-8">
                <div className="mb-14 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
                    <Reveal className="lg:col-span-8">
                        <Eyebrow>{t.services.eyebrow}</Eyebrow>
                        <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.08] tracking-tight text-brand-navy md:text-5xl lg:text-6xl">
                            {t.services.h2a} <em className="italic text-brand-olive">{t.services.h2b}</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-4">
                        <p className="text-sm leading-relaxed text-slate-500 md:text-base">{t.services.side}</p>
                    </Reveal>
                </div>

                <div data-testid="services-bento-grid" className="grid grid-cols-1 gap-5 md:grid-cols-12 md:gap-6">
                    {/* Dermatology — featured */}
                    <Reveal
                        data-testid="service-card-dermatology"
                        className="group relative overflow-hidden rounded-3xl border border-brand-accentline bg-brand-olivelight md:col-span-7"
                    >
                        <div className="flex h-full flex-col p-8 md:p-10">
                            <div className="flex items-start justify-between">
                                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-brand-olive shadow-sm">
                                    <Sparkles className="h-5 w-5" />
                                </span>
                                <Tag>{s.derm.tag}</Tag>
                            </div>
                            <h3 className="mt-6 font-display text-3xl font-semibold text-brand-navy md:text-4xl">{s.derm.title}</h3>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">{s.derm.desc}</p>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {s.derm.chips.map((c) => (
                                    <span
                                        key={c}
                                        className="rounded-full border border-brand-accentline bg-white/70 px-3 py-1.5 text-xs font-medium text-brand-navy/80"
                                    >
                                        {c}
                                    </span>
                                ))}
                            </div>
                            <div className="relative mt-8 flex-1 overflow-hidden rounded-2xl">
                                <img
                                    src={DERM_IMG}
                                    alt={`${s.derm.title} — Signita Wellness`}
                                    loading="lazy"
                                    className="h-56 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 md:h-64"
                                />
                            </div>
                        </div>
                    </Reveal>

                    {/* ENT */}
                    <Reveal
                        delay={0.1}
                        data-testid="service-card-ent"
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-brand-olive md:col-span-5"
                    >
                        <div className="flex h-full flex-col p-8">
                            <div className="flex items-start justify-between">
                                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-olivelight text-brand-olive">
                                    <Ear className="h-5 w-5" />
                                </span>
                                <Tag>{s.ent.tag}</Tag>
                            </div>
                            <h3 className="mt-6 font-display text-2xl font-semibold text-brand-navy md:text-3xl">{s.ent.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.ent.desc}</p>
                            <img
                                src={ENT_IMG}
                                alt={`${s.ent.title} — Signita Wellness`}
                                loading="lazy"
                                className="mt-6 h-36 w-full rounded-2xl object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                            />
                        </div>
                    </Reveal>

                    {/* Pathology */}
                    <Reveal
                        delay={0.05}
                        data-testid="service-card-pathology"
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-brand-olive md:col-span-5"
                    >
                        <div className="flex h-full flex-col p-8">
                            <div className="flex items-start justify-between">
                                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-olivelight text-brand-olive">
                                    <FlaskConical className="h-5 w-5" />
                                </span>
                                <Tag>{s.path.tag}</Tag>
                            </div>
                            <h3 className="mt-6 font-display text-2xl font-semibold text-brand-navy md:text-3xl">{s.path.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.path.desc}</p>
                            <img
                                src={PATH_IMG}
                                alt={`${s.path.title} — sample tubes`}
                                loading="lazy"
                                className="mt-6 h-32 w-full rounded-2xl object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                            />
                        </div>
                    </Reveal>

                    {/* ECG */}
                    <Reveal
                        delay={0.1}
                        data-testid="service-card-ecg"
                        className="rounded-3xl border border-slate-200 bg-white p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-brand-olive md:col-span-4"
                    >
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-olivelight text-brand-olive">
                            <HeartPulse className="h-5 w-5" />
                        </span>
                        <h3 className="mt-6 font-display text-2xl font-semibold text-brand-navy">{s.ecg.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.ecg.desc}</p>
                    </Reveal>

                    {/* Pharmacy — navy */}
                    <Reveal
                        delay={0.15}
                        data-testid="service-card-pharmacy"
                        className="relative overflow-hidden rounded-3xl bg-brand-navy p-8 shadow-card transition-all duration-300 hover:-translate-y-1 md:col-span-4"
                    >
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-brand-lime">
                            <Pill className="h-5 w-5" />
                        </span>
                        <h3 className="mt-6 font-display text-2xl font-semibold text-white">{s.pharm.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-300">{s.pharm.desc}</p>
                        <span className="pointer-events-none absolute -bottom-10 -right-6 font-display text-[7rem] font-semibold leading-none text-white/5">
                            Rx
                        </span>
                    </Reveal>

                    {/* OPD */}
                    <Reveal
                        delay={0.2}
                        data-testid="service-card-opd"
                        className="group rounded-3xl border border-slate-200 bg-white p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-brand-olive md:col-span-4"
                    >
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-olivelight text-brand-olive">
                            <Stethoscope className="h-5 w-5" />
                        </span>
                        <h3 className="mt-6 font-display text-2xl font-semibold text-brand-navy">{s.opd.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.opd.desc}</p>
                        <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-olive">
                            {s.opd.walkin}
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
